import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {IReduxState} from '../../../redux/types/IReduxState'
import {taskActions} from '../redux/actions'
import {Button, ButtonIcon, COLORS, Icon, MultiLink} from 'ac_ui'
import {acAlertCircleOutline, acArrowExpand} from 'ac-icon'
import {URLS} from '../../../constants/urls'
import {TaskDto, UserDto, UserSolutionDto} from 'learndb-share'
import classNames from 'classnames'
import {useRequest, UseRequestTypeEnum} from '../../../hooks/useRequest'
import {TabToggle} from '../../common/tabToggle/TabToggle'
import {TabTogglerItem} from '../../common/tabToggle/TabToggleItem'
import {MasterSolution} from './MasterSolution'
import {MySolution} from './MySolution'
import {ComponentEnum} from './Registry'
import {getTitleSplit} from '../utils'

interface ISolutionProps {
  id: string
  innerActiveComponentId: ComponentEnum
  onClickInnerToolbarTab: (currentId: ComponentEnum) => void
  splitTabs?: (leftComponentId: ComponentEnum, rightComponentId: ComponentEnum) => void
}

const Solution: React.FC<ISolutionProps> = ({innerActiveComponentId, onClickInnerToolbarTab, splitTabs}) => {
  const taskDispatch = useDispatch()
  const user = useSelector<IReduxState, UserDto | undefined>((state) => state.auth?.user)
  const task = useSelector<IReduxState, TaskDto>((state) => state.task?.task)
  const solution = useSelector<IReduxState, string | undefined>((state) => state.task?.solution)
  const [userSolution, setUserSolution] = useState<UserSolutionDto>(undefined)

  const isAuthNotAnonymous = user && !user?.isAnonymous

  const [request] = useRequest<UserSolutionDto>({
    type: UseRequestTypeEnum.API_GET,
    url: '/course/task/user-solution/' + task?.taskId,
    onDispatch: (userSolutionDb) => {
      taskDispatch(
        taskActions.setState({
          userSolution: userSolutionDb,
        }),
      )
    },
  })

  const getSolution = (): void => {
    taskDispatch(taskActions.getAnswerView(task.taskId))
  }

  const canView = task?.isSolved || task?.isAnswerViewed

  useEffect(() => {
    if (canView) {
      getSolution()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView])

  const loadUserSolution = async (): Promise<void> => {
    const userSolutionDb = await request()
    setUserSolution(userSolutionDb)
  }

  useEffect(() => {
    if (canView) {
      loadUserSolution()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView])

  const changeShowSolution = (): void => {
    getSolution()
  }

  if (solution || canView) {
    if (userSolution?.solution) {
      return (
        <section className={'d-flex flex-column align-items-start custom-scroll h-100 pt-x2 pr-x1 pl-x2 mr-x1'}>
          <div className={'d-flex mb-x2'}>
            <TabToggle>
              <TabTogglerItem
                key={ComponentEnum.masterSolution}
                id={ComponentEnum.masterSolution}
                name={'Эталонное'}
                onClick={() => {
                  onClickInnerToolbarTab(ComponentEnum.masterSolution)
                }}
                isActive={innerActiveComponentId === ComponentEnum.masterSolution}
                className={'mr-x1'}
              />
              <TabTogglerItem
                key={ComponentEnum.mySolution}
                id={ComponentEnum.mySolution}
                name={'Мое'}
                onClick={() => {
                  onClickInnerToolbarTab(ComponentEnum.mySolution)
                }}
                isActive={innerActiveComponentId === ComponentEnum.mySolution}
              />
            </TabToggle>
            {splitTabs && (
              <div className={'d-flex ml-x1'}>
                <ButtonIcon
                  title={getTitleSplit(ComponentEnum.masterSolution, ComponentEnum.mySolution)}
                  onClick={() => {
                    splitTabs(ComponentEnum.masterSolution, ComponentEnum.mySolution)
                  }}
                  iconPath={acArrowExpand}
                />
              </div>
            )}
          </div>
          {innerActiveComponentId === ComponentEnum.masterSolution ? (
            <MasterSolution id={undefined} />
          ) : (
            <MySolution id={undefined} />
          )}
        </section>
      )
    } else {
      return (
        <section className={'d-flex flex-column align-items-start custom-scroll h-100 pt-x2 pr-x1 pl-x2 mr-x1'}>
          <MasterSolution id={undefined} />
        </section>
      )
    }
  } else {
    return (
      <section className={'task-solution-question-block'}>
        <div className={'dialog-wrapper'}>
          <div className={'dialog'}>
            <p className={'dialog-title'}>Может еще подумаешь?</p>
            <div className={classNames('dialog-item', {'mb-0': task.hasSeeAnswer})}>
              <div className={'d-flex mr-x2'}>
                <Icon path={acAlertCircleOutline} color={COLORS.red} title={undefined} size={'16px'} />
              </div>
              <div className={'dialog-item-content'}>
                <p className={'dialog-item-content-name'}>Подсмотренные ответы видно в сертификате.</p>
                {isAuthNotAnonymous && (
                  <MultiLink
                    href={URLS.profile + URLS.profileCertificate}
                    className={'dialog-item-content-link mt-x1'}
                    type={'href'}>
                    Подробнее…
                  </MultiLink>
                )}
              </div>
            </div>
            {!task.hasSeeAnswer && (
              <>
                <div className={'dialog-item'}>
                  <div className={'d-flex mr-x2'}>
                    <Icon path={acAlertCircleOutline} color={COLORS.red} title={undefined} size={'16px'} />
                  </div>
                  <div className={'dialog-item-content'}>
                    <p className={'dialog-item-content-name'}>Не откроется достижение за&nbsp;прохождение темы.</p>
                  </div>
                </div>
                <div className={'dialog-item mb-0'}>
                  <div className={'d-flex mr-x2'}>
                    <Icon path={acAlertCircleOutline} color={COLORS.red} title={undefined} size={'16px'} />
                  </div>
                  <div className={'dialog-item-content'}>
                    <p className={'dialog-item-content-name'}>Не заработаешь монеты за&nbsp;выполнение достижения.</p>
                    <MultiLink
                      href={isAuthNotAnonymous ? URLS.profile + URLS.coins : URLS.coins}
                      className={'dialog-item-content-link mt-x1'}
                      type={'href'}>
                      Подробнее…
                    </MultiLink>
                  </div>
                </div>
              </>
            )}
          </div>
          <Button style={'ghost'} size={'md'} onClick={changeShowSolution} className={'w-100'}>
            Показать ответ
          </Button>
        </div>
        <div className={'robot-block'}>
          <img src={'/img/emptyList/thinkingRobot.svg'} height={'auto'} width={'100%'} alt={'Робот с вопросом'} />
        </div>
      </section>
    )
  }
}

export {Solution}
